<template>
  <div class="space-y-4">
    <!-- Plan Info Row -->
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <h4 class="font-medium text-gray-900 truncate">{{ planSource?.name || '—' }}</h4>
        <p class="text-sm text-muted-foreground mt-0.5" v-if="planSource?.description">{{ planSource.description }}</p>
      </div>
      <div class="text-right shrink-0">
        <div class="text-lg font-semibold text-gray-900">{{ planDisplayAmount }}</div>
        <div class="text-xs text-muted-foreground" v-if="planIntervalLabel">per {{ planIntervalLabel }}</div>
      </div>
    </div>

    <FreeJobSupportBanner
      v-if="freeJobSupportUntil && !showPaidJobSupportBanner"
      :formatted-date="freeJobSupportUntil"
    />
    <PaidJobSupportBanner v-if="showPaidJobSupportBanner" />

    <!-- Billing Dates -->
    <div v-if="hasBillingDates" class="grid grid-cols-2 gap-3 px-1 text-sm">
      <div>
        <p class="text-xs text-muted-foreground mb-0.5">Current Period</p>
        <p class="font-medium text-gray-900">
          {{ formatDate(subscription.current_period_start) }} – {{ formatDate(subscription.current_period_end) }}
        </p>
      </div>
      <div class="text-right">
        <p class="text-xs text-muted-foreground mb-0.5">Next Billing</p>
        <p class="font-medium text-gray-900">{{ formatDate(subscription.current_period_end) }}</p>
      </div>
    </div>

    <!-- Status Alert + Action -->
    <div
      v-if="statusAlertInfo && alertStyles"
      class="rounded-lg px-4 py-4 flex items-center justify-between gap-4"
      :class="alertStyles.container"
    >
      <div class="flex items-center gap-3 min-w-0">
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" :class="alertStyles.iconBg">
          <AlertTriangle class="w-4 h-4" :class="alertStyles.icon" />
        </div>
        <div class="min-w-0">
          <p class="text-sm font-medium" :class="alertStyles.title">{{ statusAlertInfo.title }}</p>
          <p class="text-xs mt-0.5 leading-snug" :class="alertStyles.text">
            {{ statusAlertInfo.body }}<strong v-if="statusAlertInfo.highlight" class="font-medium">{{ statusAlertInfo.highlight }}</strong><template v-if="statusAlertInfo.afterHighlight">{{ statusAlertInfo.afterHighlight }}</template>
          </p>
          <p v-if="statusAlertInfo.secondaryBody" class="text-xs mt-1" :class="alertStyles.text">{{ statusAlertInfo.secondaryBody }}</p>
        </div>
      </div>
      <button
        v-if="showPrimaryAction"
        @click="handlePrimaryAction"
        :disabled="actionLoading"
        class="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-cosmicPurple text-white text-xs font-medium rounded-md hover:bg-cosmicPurple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Loader2 v-if="actionLoading" class="w-3 h-3 animate-spin" />
        {{ actionLoading ? 'Processing…' : actionButtonLabel }}
      </button>
    </div>
    <p v-if="actionErrorMessage" class="text-xs text-destructive text-center">{{ actionErrorMessage }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { AlertTriangle, Loader2 } from 'lucide-vue-next'
import type { 
  Plan, 
  Subscription, 
  SubscriptionStatusAlertInfo, 
  SubscriptionStatusPrimaryAction 
} from '@/types/subscription'
import {
  formatPlanAmount,
  formatDate,
  resolveIntervalLabel,
  buildStatusAlert,
  resolvePrimaryAction,
  isTuitionFinished,
  getFreeJobSupportUntil,
  needsPaidJobSupport
} from '@/components/subscription/SubscriptionStatus/helpers'
import FreeJobSupportBanner from './FreeJobSupportBanner.vue'
import PaidJobSupportBanner from './PaidJobSupportBanner.vue'

interface Props {
  subscription: Subscription
  planSource?: Plan | null
  actionLoading?: boolean
  actionError?: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  subscribe: []
  'reactivate-subscription': [subscription: Subscription]
  'restore-subscription': [subscription: Subscription]
  'retry-payment': [subscription: Subscription]
}>()

const statusAlertInfo = computed<SubscriptionStatusAlertInfo | null>(() => buildStatusAlert(props.subscription))
const alertStyles = computed(() => statusAlertInfo.value?.styles ?? null)
const primaryAction = computed<SubscriptionStatusPrimaryAction | null>(() => resolvePrimaryAction(props.subscription))
const showPrimaryAction = computed(() => Boolean(primaryAction.value))

const actionLoading = computed(() => props.actionLoading ?? false)
const actionErrorMessage = computed(() => props.actionError ?? null)
const actionButtonLabel = computed(() => primaryAction.value?.label ?? '')

const planDisplayAmount = computed(() => formatPlanAmount(props.planSource ?? null))
const planIntervalLabel = computed(() => resolveIntervalLabel(props.planSource ?? null))

const freeJobSupportUntil = computed(() => getFreeJobSupportUntil(props.subscription))
const showPaidJobSupportBanner = computed(() => needsPaidJobSupport(props.subscription))

const hasBillingDates = computed(() => {
  if (isTuitionFinished(props.subscription)) return false
  return Boolean(props.subscription.current_period_start && props.subscription.current_period_end)
})

const handlePrimaryAction = () => {
  const action = primaryAction.value
  if (!action) return

  if (action.event === 'retry-payment') {
    emit('retry-payment', props.subscription)
    return
  }

  if (action.event === 'restore') {
    emit('restore-subscription', props.subscription)
    return
  }

  if (action.event === 'reactivate') {
    emit('reactivate-subscription', props.subscription)
    return
  }

  emit('subscribe')
}
</script>
